import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { useAuth } from "../../context/AuthContext";
import { CompanySwitcher } from "./AdminLayout";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, User as UserIcon, Clock, CheckCircle2, PauseCircle, Circle } from "lucide-react";
import { formatDate } from "../../lib/datetime";

const STATUSES = [
  { key: "planned",     label: "Planificado",  color: "#5B8FAF", icon: Circle },
  { key: "in_progress", label: "En progreso",  color: "#D9A557", icon: Clock },
  { key: "done",        label: "Completado",   color: "#1B7E3F", icon: CheckCircle2 },
  { key: "on_hold",     label: "En pausa",     color: "#9B1C1C", icon: PauseCircle },
];
const STATUS_BY_KEY = Object.fromEntries(STATUSES.map((s) => [s.key, s]));
const DEFAULT_STATUS = "planned";

const fmtCLP = (v, c = "CLP") =>
  c === "CLP"
    ? `$${Math.round(v || 0).toLocaleString("es-CL")}`
    : `US$${(v || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const toInputDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
const toIso = (s) => (s ? new Date(`${s}T00:00:00`).toISOString() : null);

const monthLabel = (d) =>
  d.toLocaleString("es-CL", { month: "long", year: "numeric" }).replace(/^./, (c) => c.toUpperCase());

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const daysInMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();

export default function AdminImplementations() {
  const { activeId, isAll, companies } = useCompany();
  const { isAdmin } = useAuth();
  const companyById = useMemo(
    () => companies.reduce((acc, c) => { acc[c.id] = c; return acc; }, {}),
    [companies],
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));

  const load = async () => {
    setLoading(true);
    try {
      const params = { stage: "ganado" };
      if (!isAll && activeId) params.company_id = activeId;
      const r = await api.get("/admin/prospects", { params });
      setItems(r.data);
    } catch (e) {
      toast.error("Error al cargar implementaciones");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeId, isAll]);

  const patch = async (id, fields) => {
    try {
      const r = await api.patch(`/admin/prospects/${id}`, fields);
      setItems((prev) => prev.map((p) => (p.id === id ? { ...p, ...r.data } : p)));
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  // KPIs
  const today = new Date();
  const isOverdue = (p) =>
    p.impl_end_date && new Date(p.impl_end_date) < today && p.impl_status !== "done";
  const stats = {
    total: items.length,
    planned: items.filter((p) => (p.impl_status || DEFAULT_STATUS) === "planned").length,
    in_progress: items.filter((p) => p.impl_status === "in_progress").length,
    done: items.filter((p) => p.impl_status === "done").length,
    overdue: items.filter(isOverdue).length,
  };

  // Sort: undated first (to remind to schedule), then by start_date
  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      const sa = a.impl_start_date ? new Date(a.impl_start_date).getTime() : Infinity;
      const sb = b.impl_start_date ? new Date(b.impl_start_date).getTime() : Infinity;
      if (sa !== sb) return sa - sb;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [items]);

  return (
    <div className="p-6 md:p-8" data-testid="admin-implementations">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-1">Operación</div>
          <h1 className="font-display font-bold text-2xl md:text-3xl text-ash-100 flex items-center gap-3">
            <CalendarDays size={26} className="text-brand" strokeWidth={1.5} />
            Calendario de Implementación
          </h1>
          <p className="text-ash-300 text-sm mt-2">
            Proyectos ganados que están en proceso (o por iniciar) de implementación.
          </p>
        </div>
        <CompanySwitcher />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-ink-500 border border-ink-500 mb-6">
        <Kpi label="Ganados" value={stats.total} accent />
        <Kpi label="Planificados" value={stats.planned} color="#5B8FAF" />
        <Kpi label="En progreso" value={stats.in_progress} color="#D9A557" />
        <Kpi label="Completados" value={stats.done} color="#1B7E3F" />
        <Kpi label="Atrasados" value={stats.overdue} color="#9B1C1C" />
      </div>

      {/* Timeline (month gantt) */}
      <TimelinePanel
        items={sorted}
        monthAnchor={monthAnchor}
        setMonthAnchor={setMonthAnchor}
        companyById={companyById}
      />

      {/* List view */}
      <div className="mt-6 bg-ink-800 border border-ink-500">
        <div className="px-4 py-3 border-b border-ink-500 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-ash-300 font-mono">
            Listado · {sorted.length} proyecto{sorted.length !== 1 ? "s" : ""}
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-ash-300 text-sm">Cargando…</div>
        ) : sorted.length === 0 ? (
          <div className="p-10 text-center text-ash-500 text-sm">
            Aún no hay proyectos ganados{!isAll && " para esta empresa"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-ink-700 border-b border-ink-500">
                  <Th>Proyecto</Th>
                  <Th>Empresa</Th>
                  <Th>Inicio</Th>
                  <Th>Término</Th>
                  <Th>Estado</Th>
                  <Th className="text-right">Valor</Th>
                  {isAdmin && <Th>Vendedor</Th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => {
                  const co = p.company_id ? companyById[p.company_id] : null;
                  const status = p.impl_status || DEFAULT_STATUS;
                  const overdue = isOverdue(p);
                  return (
                    <tr key={p.id} className="border-t border-ink-500 hover:bg-ink-700/40 align-top">
                      <Td>
                        <div className="font-medium text-ash-100">{p.name}</div>
                        {p.company && <div className="text-xs text-ash-500">{p.company}</div>}
                        {p.next_action && (
                          <div className="text-[11px] text-ash-300 mt-1 italic line-clamp-2">{p.next_action}</div>
                        )}
                      </Td>
                      <Td>
                        {co ? (
                          <CompanyPill co={co} />
                        ) : (
                          <span className="text-ash-500 text-xs">—</span>
                        )}
                      </Td>
                      <Td>
                        <input
                          type="date"
                          data-testid={`impl-start-${p.id}`}
                          value={toInputDate(p.impl_start_date)}
                          onChange={(e) => patch(p.id, { impl_start_date: toIso(e.target.value) })}
                          className="bg-ink-700 border border-ink-500 text-ash-100 px-2 py-1.5 text-xs font-mono focus:border-brand outline-none"
                        />
                      </Td>
                      <Td>
                        <input
                          type="date"
                          data-testid={`impl-end-${p.id}`}
                          value={toInputDate(p.impl_end_date)}
                          onChange={(e) => patch(p.id, { impl_end_date: toIso(e.target.value) })}
                          className={`bg-ink-700 border ${overdue ? "border-[#9B1C1C]" : "border-ink-500"} text-ash-100 px-2 py-1.5 text-xs font-mono focus:border-brand outline-none`}
                        />
                        {overdue && (
                          <div className="text-[10px] text-[#FF6B6B] mt-1 uppercase tracking-widest">Atrasado</div>
                        )}
                      </Td>
                      <Td>
                        <select
                          data-testid={`impl-status-${p.id}`}
                          value={status}
                          onChange={(e) => patch(p.id, { impl_status: e.target.value })}
                          className="bg-ink-700 border border-ink-500 text-ash-100 px-2 py-1.5 text-xs font-mono focus:border-brand outline-none"
                          style={{ borderLeft: `4px solid ${STATUS_BY_KEY[status]?.color || "#7BA6B0"}` }}
                        >
                          {STATUSES.map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                          ))}
                        </select>
                      </Td>
                      <Td className="text-right font-mono text-brand">
                        {fmtCLP(p.value, p.currency)}
                      </Td>
                      {isAdmin && (
                        <Td>
                          {p.owner_name ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-ash-100">
                              <UserIcon size={11} className="text-brand-slate" strokeWidth={1.6} />
                              {p.owner_name}
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-widest text-ash-500 font-mono">Sin asignar</span>
                          )}
                        </Td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, color }) {
  return (
    <div className={`p-4 ${accent ? "bg-ink-700" : "bg-ink-800"}`}>
      <div className="font-display font-black text-ash-100 tracking-tighter text-3xl leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest mt-2 font-mono flex items-center gap-2">
        {color && <span className="w-2 h-2" style={{ background: color }} />}
        <span className="text-ash-300">{label}</span>
      </div>
    </div>
  );
}

function CompanyPill({ co }) {
  const col = co.color || "#7BA6B0";
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold text-white shadow-sm"
      style={{ background: col, boxShadow: `0 0 0 1px ${col}, 0 0 8px ${col}55` }}
    >
      <span className="w-1.5 h-1.5 bg-white/80" />
      <span className="truncate max-w-[140px]">{co.name}</span>
    </span>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left text-[10px] uppercase tracking-widest text-ash-300 font-mono ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}

/* ---------------- Timeline ---------------- */
function TimelinePanel({ items, monthAnchor, setMonthAnchor, companyById }) {
  const start = monthAnchor;
  const total = daysInMonth(start);
  const endMs = new Date(start.getFullYear(), start.getMonth(), total, 23, 59, 59).getTime();
  const startMs = start.getTime();
  const todayDay = (() => {
    const t = new Date();
    if (t.getFullYear() === start.getFullYear() && t.getMonth() === start.getMonth()) return t.getDate();
    return null;
  })();

  // Filter items that intersect this month
  const visible = items.filter((p) => {
    if (!p.impl_start_date && !p.impl_end_date) return false;
    const s = p.impl_start_date ? new Date(p.impl_start_date).getTime() : (p.impl_end_date ? new Date(p.impl_end_date).getTime() : null);
    const e = p.impl_end_date ? new Date(p.impl_end_date).getTime() : (p.impl_start_date ? new Date(p.impl_start_date).getTime() : null);
    if (s === null || e === null) return false;
    return s <= endMs && e >= startMs;
  });

  const prev = () => setMonthAnchor(new Date(start.getFullYear(), start.getMonth() - 1, 1));
  const next = () => setMonthAnchor(new Date(start.getFullYear(), start.getMonth() + 1, 1));
  const goToday = () => setMonthAnchor(startOfMonth(new Date()));

  return (
    <div className="bg-ink-800 border border-ink-500">
      <div className="flex items-center justify-between px-4 py-3 border-b border-ink-500">
        <div className="text-[11px] uppercase tracking-widest text-ash-300 font-mono">
          Línea de tiempo
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prev} className="border border-ink-500 hover:border-brand hover:text-brand p-1.5"><ChevronLeft size={14} /></button>
          <div className="text-sm text-ash-100 font-medium px-3 min-w-[160px] text-center">{monthLabel(start)}</div>
          <button onClick={next} className="border border-ink-500 hover:border-brand hover:text-brand p-1.5"><ChevronRight size={14} /></button>
          <button onClick={goToday} className="ml-2 border border-ink-500 hover:border-brand hover:text-brand px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono">
            Hoy
          </button>
        </div>
      </div>

      {/* Day header */}
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid border-b border-ink-500" style={{ gridTemplateColumns: `220px repeat(${total}, minmax(0, 1fr))` }}>
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-ash-500 font-mono border-r border-ink-500">Proyecto</div>
            {Array.from({ length: total }, (_, i) => i + 1).map((d) => (
              <div
                key={d}
                className={`text-center text-[10px] py-2 font-mono ${
                  todayDay === d ? "bg-brand/10 text-brand font-bold" : "text-ash-500"
                }`}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Rows */}
          {visible.length === 0 ? (
            <div className="px-4 py-10 text-center text-ash-500 text-sm">
              Ningún proyecto agendado en {monthLabel(start)}.
            </div>
          ) : (
            visible.map((p) => {
              const s = new Date(p.impl_start_date || p.impl_end_date).getTime();
              const e = new Date(p.impl_end_date || p.impl_start_date).getTime();
              // Compute columns inside month
              const clampedStart = Math.max(s, startMs);
              const clampedEnd = Math.min(e, endMs);
              const startCol = new Date(clampedStart).getDate();
              const endCol = new Date(clampedEnd).getDate();
              const colSpan = Math.max(1, endCol - startCol + 1);
              const status = p.impl_status || DEFAULT_STATUS;
              const color = STATUS_BY_KEY[status]?.color || "#7BA6B0";
              const co = p.company_id ? companyById[p.company_id] : null;

              return (
                <div
                  key={p.id}
                  className="grid border-b border-ink-500/70 hover:bg-ink-700/30"
                  style={{ gridTemplateColumns: `220px repeat(${total}, minmax(0, 1fr))` }}
                >
                  <div className="px-3 py-2 border-r border-ink-500 min-w-0">
                    <div className="text-xs text-ash-100 truncate">{p.name}</div>
                    {co && (
                      <div className="mt-1">
                        <CompanyPill co={co} />
                      </div>
                    )}
                  </div>
                  {Array.from({ length: total }, (_, i) => i + 1).map((d) => {
                    if (d === startCol) {
                      return (
                        <div
                          key={d}
                          className="my-1.5 mx-px h-6 text-[10px] text-white font-bold flex items-center px-2 overflow-hidden"
                          style={{
                            gridColumn: `span ${colSpan} / span ${colSpan}`,
                            background: color,
                            boxShadow: `0 0 0 1px ${color}, 0 0 6px ${color}55`,
                          }}
                          title={`${formatDate(p.impl_start_date)} → ${formatDate(p.impl_end_date)} · ${STATUS_BY_KEY[status]?.label}`}
                        >
                          <span className="truncate">{STATUS_BY_KEY[status]?.label}</span>
                        </div>
                      );
                    }
                    if (d > startCol && d <= endCol) return null;
                    return (
                      <div
                        key={d}
                        className={`border-r border-ink-500/40 ${todayDay === d ? "bg-brand/5" : ""}`}
                      />
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
